import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "react-query";

import AddToCartButton from "../../../components/customButtons/AddToCartButton";
import BookmarkButton from "../../../components/customButtons/BookmarkButton";
import useUser from "../../../hooks/useUser";
import useAddCartItem from "../../../hooks/useAddCartItem";
import useGetCart from "../../../hooks/useGetCart";
import useSnackbar from "../../../components/snackbar/hooks/useSnackbar";
import useGetAdminConfigs from "../../../hooks/useGetAdminConfigs";
import { formatToCurrency } from "../../../utils/numbers";
import { CART_ITEMS_TYPES } from "../../../constants/cartItems";
import { updateDesign } from "../../../apis/designsApi";
import { DesignState, markAsUnchanged } from "../../../features/designSlice";
import { CART_ITEMS_QUERY_KEY } from "../../../queries-keys/cartItems";
import { getErrorMessage } from "../../../utils/errors";
import { CURRENCY } from "../../../constants/common";
import SvgIcon from "../../../components/svgIcon";
import { DELIVERY_ICON_URL } from "../../../constants/iconsUrls";
import ConfigurationSettings from "../../../models/ConfigurationSettings";
import Design from "../../../models/Design";
import Element from "../../../models/furniture/Element";
import Material from "../../../models/furniture/Material";
import { checkCompositionIntegrity } from "../../../utils/designs";
import Alert from "../../../components/alert";

function DesignSummary({ elements }: { elements: Element[] }) {
  const dispatch = useDispatch();
  const {
    id: designId,
    isChanged: isDesignChanged,
    image,
    layout_body: selectedLayoutBody,
    layout_size: selectedLayoutSize,
    price,
    priceRangePercentage,
    priceMode,
    shapes: selectedShapes,
    materials: selectedMaterials,
    composition,
  } = useSelector((state: { design: DesignState }) => state.design);

  const queryClient = useQueryClient();
  const { user } = useUser();
  const { cartItems } = useGetCart();
  const { handleOpenSnackbar } = useSnackbar();

  const { configs } = useGetAdminConfigs();

  const { shippingNotes, designExpectedDeliveryDuration } =
    configs as ConfigurationSettings;

  const { addCartItemMutation, addCartItemMutationAsync } = useAddCartItem();

  const processedElements = useMemo(() => {
    return Object.entries(selectedShapes).map(([elementOrderStr, shape]) => {
      const elementOrder = parseInt(elementOrderStr);
      const elementIndex = elementOrder - 1;
      const element = elements[elementIndex];

      const elementMaterials: Record<number, Material> = {};
      Object.entries(selectedMaterials).forEach(([materialKey, material]) => {
        const [elemOrder, materialOrder] = materialKey.split("_").map(Number);
        if (elemOrder === elementOrder) {
          elementMaterials[materialOrder] = material;
        }
      });

      return {
        elementOrder,
        elementName: element?.name || `Element ${elementOrder}`,
        shape,
        materials: elementMaterials,
      };
    });
  }, [selectedShapes, selectedMaterials, elements]);

  const { mutate: updateDesignMutation } = useMutation(
    (updatedData: Partial<Design>) => updateDesign(designId || 0, updatedData),
    {
      onError: (error) => {
        handleOpenSnackbar(getErrorMessage(error), "error");
      },
      onSuccess: () => {
        dispatch(markAsUnchanged());
        queryClient.invalidateQueries(CART_ITEMS_QUERY_KEY(user?.id));
        handleOpenSnackbar("Your design has been saved!", "success");
      },
    }
  );

  const { mutate: updateAndAddDesignToCartMutation } = useMutation(
    async (updatedData: Partial<Design>) => {
      await updateDesign(designId || 0, updatedData);

      dispatch(markAsUnchanged());
      queryClient.invalidateQueries(CART_ITEMS_QUERY_KEY(user?.id));

      return await addCartItemMutationAsync({
        design: designId,
        type: CART_ITEMS_TYPES.DESIGN,
        quantity: 1,
      });
    },
    {
      onError: (error) => {
        handleOpenSnackbar(getErrorMessage(error), "error");
      },
      onSuccess: () => {
        handleOpenSnackbar(
          "Your design has been added to your cart!",
          "success"
        );
      },
    }
  );

  const handleDesignSaving = () => {
    if (!isDesignChanged) {
      return;
    }

    updateDesignMutation({
      layout_body: selectedLayoutBody,
      layout_size: selectedLayoutSize,
      composition,
      shapes: selectedShapes,
      materials: selectedMaterials,
      image,
      price,
    });
  };

  const handleAddToCart = () => {
    const found = cartItems.some(
      (cartItem: any) =>
        cartItem?.type === CART_ITEMS_TYPES.DESIGN &&
        cartItem[cartItem?.type]?.id === designId
    );

    if (found) {
      return handleDesignSaving();
    }

    if (isDesignChanged) {
      return updateAndAddDesignToCartMutation({
        layout_body: selectedLayoutBody,
        layout_size: selectedLayoutSize,
        composition,
        shapes: selectedShapes,
        materials: selectedMaterials,
        image,
        price,
      });
    }

    addCartItemMutation({
      design: designId,
      type: CART_ITEMS_TYPES.DESIGN,
      quantity: 1,
    });
  };

  const isCompositionIntegrityValid = checkCompositionIntegrity({
    composition,
    shapes: selectedShapes,
    materials: selectedMaterials,
    layout_body: selectedLayoutBody,
    layout_size: selectedLayoutSize,
  });

  return (
    <div className="flex flex-col space-y-5 text-primary">
      <Alert
        isOpen={!isCompositionIntegrityValid}
        message={"Please complete your design selections before adding to cart"}
        severity={"info"}
      />
      <div className="flex items-center justify-between">
        <span className="text-[18px] font-bold">Summary</span>
        {/* <div className="flex items-center space-x-2">
          <FiMinusSquare
            className="cursor-pointer text-[25px] text-highlight"
            onClick={() => setCount((prev) => Math.max(1, prev - 1))}
          />
          <span className="font-medium">{count}</span>
          <FiPlusSquare
            className="cursor-pointer text-[25px] text-highlight"
            onClick={() => setCount((prev) => prev + 1)}
          />
        </div> */}
      </div>

      <div className="flex flex-col space-y-3">
        {/* Layout Section */}
        {selectedLayoutBody && selectedLayoutSize && (
          <div className="flex flex-col space-y-1">
            <span className="text-[14px] font-bold">Layout</span>
            <div className="flex items-center justify-between">
              <span>
                {selectedLayoutSize.name}
                {selectedLayoutSize.dimensions && (
                  <span> ({selectedLayoutSize.dimensions})</span>
                )}
              </span>
              <span>
                {formatToCurrency(
                  selectedLayoutSize.price,
                  CURRENCY,
                  +selectedLayoutSize.range_price_percentage,
                  priceMode
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>{selectedLayoutBody.name}</span>
              <span>
                {formatToCurrency(
                  selectedLayoutBody.price,
                  CURRENCY,
                  +selectedLayoutBody.range_price_percentage,
                  priceMode
                )}
              </span>
            </div>
          </div>
        )}

        {processedElements.map(
          ({ elementOrder, elementName, shape, materials }) => (
            <React.Fragment key={elementOrder}>
              <hr />
              <div className="flex flex-col space-y-1">
                <span className="text-[14px] font-bold">{elementName}</span>

                <div className="flex items-center justify-between">
                  <span>{shape.name}</span>
                  <span>
                    {formatToCurrency(
                      shape.price,
                      CURRENCY,
                      +(shape.range_price_percentage || 0),
                      priceMode
                    )}
                  </span>
                </div>

                {Object.entries(materials).map(
                  ([materialOrderStr, material]) => (
                    <div
                      key={materialOrderStr}
                      className="flex items-center justify-between"
                    >
                      <span>{material.name}</span>
                      <span>
                        {formatToCurrency(
                          material.price,
                          CURRENCY,
                          +(material.range_price_percentage || 0),
                          priceMode
                        )}
                      </span>
                    </div>
                  )
                )}
              </div>
            </React.Fragment>
          )
        )}
      </div>

      <div className="flex items-center justify-between text-primary">
        <span className="text-[18px] font-medium">Total Price</span>
        <span className="text-[32px] font-bold">
          {formatToCurrency(price, CURRENCY, priceRangePercentage, priceMode)}
        </span>
      </div>

      <div className="flex justify-end space-x-4 pt-4">
        <BookmarkButton
          onClick={handleDesignSaving}
          disabled={!isDesignChanged}
        />

        <AddToCartButton
          onClick={handleAddToCart}
          className="w-full lg:w-[180px]"
          disabled={!isCompositionIntegrityValid}
        />
      </div>

      <div className="flex flex-col space-y-1">
        <div className="mx-auto flex items-center space-x-3">
          <SvgIcon
            src={DELIVERY_ICON_URL}
            className={"h-[40px] text-primary"}
          />
          <span>Ready In {designExpectedDeliveryDuration} weeks</span>
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-[14px]">Please Note:</span>
          <span className="text-[14px]">{shippingNotes}</span>
        </div>
      </div>
    </div>
  );
}

export default DesignSummary;
